import { FC, useMemo, useState } from 'react';
import { isRecord } from '@repo/shared';
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart as RechartsLineChart,
  ReferenceArea,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ChartExportButton,
  ChartExportHeader,
} from '@/components/charts/chart-export-button';
import {
  ChartExportFilter,
  LineConfig,
  RegionConfig,
  TabConfig,
} from '@/components/charts/types';
import { useChartExport } from '@/components/charts/use-chart-export';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { ChartConfig } from '@/components/ui/chart';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { cn } from '@/lib/utils';

interface LineChartProps {
  data: Array<Record<string, unknown>>;
  indexKey: string;
  title: string;
  tabs: Array<TabConfig>;
  lines?: Array<LineConfig>;
  regions?: Array<RegionConfig>;
  description?: string;
  height?: number;
  className?: string;
  yAxisTicks?: boolean;
  yAxisDomain?: [number | string, number | string];
  showTabTotals?: boolean;
  showLegend?: boolean;
  tooltipContent?: React.ComponentProps<typeof ChartTooltip>['content'];
  exportFilters?: Array<ChartExportFilter>;
}

export const LineChart: FC<LineChartProps> = ({
  data,
  indexKey,
  title,
  description,
  tabs,
  lines,
  regions,
  height = 250,
  className,
  yAxisTicks = false,
  yAxisDomain,
  showTabTotals = true,
  showLegend = true,
  tooltipContent,
  exportFilters,
}) => {
  const [activeTab, setActiveTab] = useState<TabConfig | undefined>(tabs[0]);

  const activeTabLabel = activeTab?.label ?? '';
  const { captureRef, exportChart, isExporting } = useChartExport({
    title,
    activeTabLabel,
    exportFilters,
  });

  const hasLines = lines && lines.length > 0;

  const tabTotals = useMemo(
    () =>
      tabs.reduce<Record<string, number>>((totals, tab) => {
        const total = data.reduce((sum, item) => {
          if (hasLines) {
            const categoryValue = item[tab.key];
            if (!isRecord<number>(categoryValue)) return sum;
            const subTotal = lines.reduce((lineSum, line) => {
              const lineValue = categoryValue[line.key];
              return lineSum + (lineValue ?? 0);
            }, 0);
            return sum + subTotal;
          }
          const value = item[tab.key];
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);

        return { ...totals, [tab.key]: total };
      }, {}),
    [data, tabs, lines, hasLines]
  );

  const chartConfig = useMemo((): ChartConfig => {
    if (hasLines) {
      return lines.reduce<ChartConfig>(
        (config, line) => ({
          ...config,
          [`${activeTab?.key}.${line.key}`]: {
            label: line.label,
            color: line.color,
          },
        }),
        {}
      );
    }

    return tabs.reduce<ChartConfig>(
      (config, tab, index) => ({
        ...config,
        [tab.key]: {
          label: tab.label,
          color: tab.color ?? `hsl(var(--chart-${(index % 5) + 1}))`,
        },
      }),
      {}
    );
  }, [tabs, lines, hasLines, activeTab?.key]);

  return (
    <Card className={cn('group gap-0 py-0', className)}>
      <CardHeader className='flex flex-col items-stretch border-b !p-0 sm:flex-row'>
        <div className='flex flex-1 flex-col justify-center gap-1 px-6 pt-4 pb-3 sm:!py-0'>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>

        <div className='flex flex-wrap sm:flex-nowrap'>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              data-active={activeTab?.key === tab.key}
              className='data-[active=true]:bg-muted/50 relative z-30 flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l sm:border-t-0 sm:border-l sm:px-8 sm:py-6 hover:bg-muted/20 transition-colors'
              onClick={() => {
                setActiveTab(tab);
              }}>
              <span
                className={cn(
                  'text-muted-foreground text-xs',
                  !showTabTotals && 'text-lg font-bold text-black'
                )}>
                {tab.label}
              </span>
              {showTabTotals && (
                <span className='text-lg leading-none font-bold sm:text-3xl'>
                  {(tabTotals[tab.key] ?? 0).toLocaleString()}
                </span>
              )}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className='relative px-2 pt-10 pb-4 sm:px-6 sm:pt-10 sm:pb-6'>
        <ChartExportButton
          onClick={exportChart}
          isExporting={isExporting}
        />
        <div
          ref={captureRef}
          className='bg-background'>
          <div
            data-chart-export-header
            className='hidden'>
            <ChartExportHeader
              title={title}
              description={description}
              activeTabLabel={activeTabLabel}
              exportFilters={exportFilters}
            />
          </div>
          <ChartContainer
            config={chartConfig}
            className='aspect-auto w-full min-h-[50px]'
            style={{ height: `${height}px` }}>
            <RechartsLineChart
              accessibilityLayer
              data={data}
              margin={{ left: 12, right: 12 }}>
              <CartesianGrid vertical={false} />
              <YAxis
                tickLine={yAxisTicks}
                axisLine={yAxisTicks}
                tickMargin={8}
                domain={yAxisDomain}
              />
              <XAxis
                dataKey={indexKey}
                tickLine={true}
                axisLine={true}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value: string) =>
                  new Date(value).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })
                }
              />

              <ChartTooltip
                content={
                  tooltipContent ?? (
                    <ChartTooltipContent
                      nameKey={!hasLines ? activeTab?.key : undefined}
                    />
                  )
                }
              />

              {hasLines && showLegend && (
                <ChartLegend content={<ChartLegendContent />} />
              )}

              {regions?.map((region, index) => (
                <ReferenceArea
                  key={`region-${index}`}
                  x1={region.x1}
                  x2={region.x2}
                  y1={region.y1}
                  y2={region.y2}
                  fill={region.fill ?? 'hsl(var(--muted))'}
                  fillOpacity={region.fillOpacity ?? 0.3}
                  stroke={region.stroke}
                  strokeDasharray={region.strokeDasharray}
                  label={region.label}
                />
              ))}

              {hasLines ? (
                lines.map((line) => (
                  <Line
                    key={line.key}
                    type={line.curveType ?? 'monotone'}
                    dataKey={`${activeTab?.key ?? ''}.${line.key}`}
                    name={line.label}
                    stroke={line.color}
                    strokeWidth={line.strokeWidth ?? 2}
                    strokeOpacity={line.strokeOpacity ?? 1}
                    strokeDasharray={line.strokeDasharray}
                    dot={line.dot ?? false}>
                    {line.showLabelList && (
                      <LabelList
                        dataKey={`${activeTab?.key ?? ''}.${line.key}`}
                        position='top'
                        fontSize={11}
                        formatter={(value: number) => `${Math.round(value)}%`}
                      />
                    )}
                  </Line>
                ))
              ) : (
                <Line
                  type='monotone'
                  dataKey={activeTab?.key ?? ''}
                  stroke={`var(--color-${activeTab?.key ?? ''})`}
                  strokeWidth={2}
                  dot={false}
                />
              )}
            </RechartsLineChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
};
